"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import { Input } from "@baluu/ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@baluu/ui/components/popover";
import {
  COUNTRY_CODES,
  getDefaultCountry,
  parsePhoneNumber,
  type CountryCode,
} from "@baluu/ui/lib/country-codes";

type PhoneCountryInputProps = {
  value: string;
  onChange: (fullPhone: string) => void;
  onBlur?: () => void;
  defaultCountryCode?: string;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  className?: string;
  container?: HTMLElement | null;
};

function PhoneCountryInput({
  value,
  onChange,
  onBlur,
  defaultCountryCode,
  disabled,
  hasError,
  placeholder = "Enter phone number",
  className,
  container,
}: PhoneCountryInputProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const defaultCountry = React.useMemo(
    () => getDefaultCountry(defaultCountryCode),
    [defaultCountryCode]
  );

  // Parse value into country + national number
  const { selectedCountry, nationalNumber } = React.useMemo(() => {
    if (!value) {
      return { selectedCountry: defaultCountry, nationalNumber: "" };
    }
    const parsed = parsePhoneNumber(value);
    return {
      selectedCountry: parsed.country || defaultCountry,
      nationalNumber: parsed.nationalNumber,
    };
  }, [value, defaultCountry]);

  const filteredCountries = React.useMemo(() => {
    if (!searchQuery) return COUNTRY_CODES;
    const q = searchQuery.toLowerCase();
    return COUNTRY_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  function handleCountrySelect(country: CountryCode) {
    setOpen(false);
    setSearchQuery("");
    onChange(`${country.dialCode}${nationalNumber}`);
  }

  function handleNumberChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/[^\d]/g, "");
    if (digits) {
      onChange(`${selectedCountry.dialCode}${digits}`);
    } else {
      onChange("");
    }
  }

  return (
    <div className={cn("flex gap-2", className)}>
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen) {
            setSearchQuery("");
            setTimeout(() => searchInputRef.current?.focus(), 0);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select country code"
            className="w-[110px] shrink-0 justify-between px-2"
            disabled={disabled}
          >
            <div className="flex items-center gap-1.5 overflow-hidden">
              <span className="text-base">{selectedCountry.flag}</span>
              <span className="text-xs truncate">
                {selectedCountry.dialCode}
              </span>
            </div>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[260px] p-0"
          align="start"
          container={container}
        >
          <div className="p-2 border-b">
            <Input
              ref={searchInputRef}
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto">
            {filteredCountries.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No countries found
              </div>
            )}
            {filteredCountries.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountrySelect(country)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted cursor-pointer",
                  country.code === selectedCountry.code && "bg-muted/60"
                )}
              >
                <span className="text-base">{country.flag}</span>
                <span className="flex-1 truncate text-left">
                  {country.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {country.dialCode}
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        placeholder={placeholder}
        value={nationalNumber}
        onChange={handleNumberChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={hasError}
        className="flex-1"
      />
    </div>
  );
}

export { PhoneCountryInput, type PhoneCountryInputProps };
